import axios from 'axios';
import { API_BASE_URL } from '../config/environment';

// Remove trailing slash from base URL to prevent double-slash issues
const API_URL = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;

export interface ProxmoxHost {
  id: number;
  name: string;
  hostname: string;
  port: number;
  username: string;
  ipv6_block: string;
  ipv6_gateway: string;
  datacenter_location: string;
  current_vms: number;
  max_vms: number;
  // Resource capacity
  cpu_cores?: number;
  ram_gb?: number;
  storage_gb?: number;
  // Allocated resources (enterprise capacity management)
  allocated_cpu_cores?: number;
  allocated_ram_gb?: number;
  allocated_storage_gb?: number;
  status: 'active' | 'maintenance' | 'offline';
  is_default: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
}

// Capacity overview for enterprise management
export interface CapacityOverview {
  totalHosts: number;
  activeHosts: number;
  totalCapacity: {
    cpu_cores: number;
    ram_gb: number;
    storage_gb: number;
    max_vms: number;
  };
  allocatedResources: {
    cpu_cores: number;
    ram_gb: number;
    storage_gb: number;
    current_vms: number;
  };
  utilizationPercent: {
    cpu: number;
    ram: number;
    storage: number;
    vms: number;
  };
  hostsNeedingAttention: ProxmoxHost[];
}

export interface ProxmoxHostStats {
  totalHosts: number;
  activeHosts: number;
  maintenanceHosts: number;
  offlineHosts: number;
  totalVMs: number;
  totalCapacity: number;
  utilizationPercentage: number;
}

export interface CreateProxmoxHostData {
  name: string;
  hostname: string;
  port: number;
  username: string;
  password: string;
  ipv6_block: string;
  ipv6_gateway: string;
  datacenter_location: string;
  max_vms: number;
  is_default?: boolean;
  notes?: string;
}

export interface UpdateProxmoxHostData {
  name?: string;
  hostname?: string;
  port?: number;
  username?: string;
  password?: string;
  ipv6_block?: string;
  ipv6_gateway?: string;
  datacenter_location?: string;
  max_vms?: number;
  notes?: string;
}

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
};

export const getAllProxmoxHosts = async (): Promise<ProxmoxHost[]> => {
  const response = await axios.get(`${API_URL}/api/admin/proxmox-hosts`, getAuthHeader());
  return response.data.data || response.data.hosts || [];
};

export const getActiveProxmoxHosts = async (): Promise<ProxmoxHost[]> => {
  const response = await axios.get(`${API_URL}/api/admin/proxmox-hosts/active`, getAuthHeader());
  return response.data.data || response.data.hosts || [];
};

export const getProxmoxHostById = async (id: number): Promise<ProxmoxHost> => {
  const response = await axios.get(`${API_URL}/api/admin/proxmox-hosts/${id}`, getAuthHeader());
  return response.data.data || response.data.host;
};

export const getProxmoxHostStats = async (id: number): Promise<any> => {
  const response = await axios.get(`${API_URL}/api/admin/proxmox-hosts/${id}/stats`, getAuthHeader());
  return response.data.data || response.data.stats;
};

export const getOverviewStats = async (): Promise<ProxmoxHostStats> => {
  const response = await axios.get(`${API_URL}/api/admin/proxmox-hosts/stats/overview`, getAuthHeader());
  const data = response.data.data || response.data.stats || response.data;
  return {
    totalHosts: parseInt(data.total_hosts || data.totalHosts || 0),
    activeHosts: parseInt(data.active_hosts || data.activeHosts || 0),
    maintenanceHosts: parseInt(data.maintenance_hosts || data.maintenanceHosts || 0),
    offlineHosts: parseInt(data.offline_hosts || data.offlineHosts || 0),
    totalVMs: parseInt(data.total_used || data.totalVMs || 0),
    totalCapacity: parseInt(data.total_capacity || data.totalCapacity || 0),
    utilizationPercentage: parseFloat(data.avg_utilization_pct || data.utilizationPercentage || 0)
  };
};

export const selectBestHost = async (): Promise<ProxmoxHost> => {
  const response = await axios.get(`${API_URL}/api/admin/proxmox-hosts/select-best`, getAuthHeader());
  return response.data.data || response.data.host;
};

export const createProxmoxHost = async (data: CreateProxmoxHostData): Promise<ProxmoxHost> => {
  const response = await axios.post(`${API_URL}/api/admin/proxmox-hosts`, data, getAuthHeader());
  return response.data.data || response.data.host;
};

export const updateProxmoxHost = async (id: number, data: UpdateProxmoxHostData): Promise<ProxmoxHost> => {
  const response = await axios.put(`${API_URL}/api/admin/proxmox-hosts/${id}`, data, getAuthHeader());
  return response.data.data || response.data.host;
};

export const deleteProxmoxHost = async (id: number): Promise<void> => {
  await axios.delete(`${API_URL}/api/admin/proxmox-hosts/${id}`, getAuthHeader());
};

export const testProxmoxCredentials = async (data: { hostname: string; port: number; username: string; password: string }): Promise<{ success: boolean; message: string }> => {
  const response = await axios.post(`${API_URL}/api/admin/proxmox-hosts/test-credentials`, data, getAuthHeader());
  return response.data;
};

export const testProxmoxConnection = async (id: number): Promise<{ success: boolean; message: string; stats?: any }> => {
  const response = await axios.post(`${API_URL}/api/admin/proxmox-hosts/${id}/test-connection`, {}, getAuthHeader());
  return response.data;
};

export const setDefaultHost = async (id: number): Promise<void> => {
  await axios.post(`${API_URL}/api/admin/proxmox-hosts/${id}/set-default`, {}, getAuthHeader());
};

export const updateHostStatus = async (id: number, status: 'active' | 'maintenance' | 'offline'): Promise<ProxmoxHost> => {
  const response = await axios.patch(`${API_URL}/api/admin/proxmox-hosts/${id}/status`, { status }, getAuthHeader());
  return response.data.data || response.data.host;
};

export const getHostVMs = async (id: number): Promise<any[]> => {
  const response = await axios.get(`${API_URL}/api/admin/proxmox-hosts/${id}/vms`, getAuthHeader());
  return response.data.data?.vms || response.data.vms || [];
};

export const syncVMCount = async (id: number): Promise<{ current_vms: number }> => {
  const response = await axios.post(`${API_URL}/api/admin/proxmox-hosts/${id}/sync-vm-count`, {}, getAuthHeader());
  return response.data.data || response.data;
};

export const updateHostResources = async (id: number): Promise<any> => {
  const response = await axios.post(`${API_URL}/api/admin/proxmox-hosts/${id}/update-resources`, {}, getAuthHeader());
  return response.data.data || response.data.stats;
};

// ==========================================
// ENTERPRISE CAPACITY MANAGEMENT
// ==========================================

export const getCapacityOverview = async (): Promise<CapacityOverview> => {
  const response = await axios.get(`${API_URL}/api/admin/proxmox-hosts/capacity/overview`, getAuthHeader());
  return response.data.data;
};

export const syncAllHostResources = async (): Promise<{
  totalHosts: number;
  hostsUpdated: number;
  results: Array<{
    hostId: number;
    hostName: string;
    updated: boolean;
    oldValues: { cpu: number; ram: number; storage: number; vms: number };
    newValues: { cpu: number; ram: number; storage: number; vms: number };
  }>;
}> => {
  const response = await axios.post(`${API_URL}/api/admin/proxmox-hosts/sync-all-resources`, {}, getAuthHeader());
  return response.data.data;
};

export const syncHostResources = async (id: number): Promise<{
  oldValues: { cpu: number; ram: number; storage: number; vms: number };
  newValues: { cpu: number; ram: number; storage: number; vms: number };
  updated: boolean;
}> => {
  const response = await axios.post(`${API_URL}/api/admin/proxmox-hosts/${id}/sync-resources`, {}, getAuthHeader());
  return response.data.data;
};

export const checkHostCapacity = async (id: number, requirements: {
  cpu_cores: number;
  memory_mb: number;
  disk_gb: number;
}): Promise<{
  canProvision: boolean;
  host: string;
  requirements: { cpu_cores: number; memory_gb: number; disk_gb: number };
  available: { cpu_cores: number; ram_gb: number; storage_gb: number; vms: number };
  afterProvisioning: { ram_percent: number; storage_percent: number; vms: number };
  warnings: string[];
}> => {
  const response = await axios.post(`${API_URL}/api/admin/proxmox-hosts/${id}/check-capacity`, requirements, getAuthHeader());
  return response.data.data;
};

export const findBestHostForRequirements = async (requirements: {
  cpu_cores: number;
  memory_mb: number;
  disk_gb: number;
}): Promise<{
  host: ProxmoxHost;
  capacityStatus: any;
  warnings: string[];
}> => {
  const response = await axios.post(`${API_URL}/api/admin/proxmox-hosts/find-best-for-requirements`, requirements, getAuthHeader());
  return response.data.data;
};
